import { useQuery } from "@tanstack/react-query";
import { tavus, type TavusFace, type TavusVoice } from "./client";
import { createLogger } from "@/lib/frontend-logger";
import { TAVUS_FACES_QUERY_KEY } from "./faceCache";

const logger = createLogger("tavus.options");

export type TavusFaceOption = {
  id: string;
  label: string;
  thumbnail_url?: string;
  face_type?: string;
};

export type ElevenLabsVoiceOption = {
  id: string;
  label: string;
  preview_url?: string;
  gender?: string;
  accent?: string;
};

function normalizeFace(f: TavusFace): TavusFaceOption | null {
  const id = String(f.face_id || f.replica_id || "").trim();
  if (!id) return null;
  const label = String(f.face_name || f.replica_name || f.name || id).trim();
  const nested =
    f.thumbnail && typeof f.thumbnail === "object"
      ? (f.thumbnail.image_url || f.thumbnail.url)
      : typeof f.thumbnail === "string"
        ? f.thumbnail
        : undefined;
  const video = typeof f.thumbnail_video_url === "string" ? f.thumbnail_video_url : undefined;
  const isImage = (u?: string) => !!u && !/\.(mp4|webm|mov)(\?|$)/i.test(u);
  const thumbnail_url =
    (typeof f.thumbnail_image_url === "string" && f.thumbnail_image_url) ||
    (typeof f.thumbnail_url === "string" && f.thumbnail_url) ||
    (typeof nested === "string" && nested) ||
    (typeof f.gif_url === "string" && f.gif_url) ||
    (isImage(video) ? video : undefined) ||
    undefined;
  return {
    id,
    label,
    thumbnail_url: thumbnail_url || undefined,
    face_type: (f.face_type as string) || (f.replica_type as string) || undefined,
  };
}

export function useTavusFaces(opts?: { enabled?: boolean }) {
  const q = useQuery({
    queryKey: TAVUS_FACES_QUERY_KEY,
    queryFn: () => tavus.listFaces(),
    staleTime: 5 * 60 * 1000,
    enabled: opts?.enabled ?? true,
  });
  const raw = q.data?.faces ?? [];
  const options: TavusFaceOption[] = raw
    .map(normalizeFace)
    .filter((x): x is TavusFaceOption => x !== null);
  if (raw.length && options.length) {
    const withThumbs = options.filter((o) => o.thumbnail_url).length;
    if (withThumbs < options.length) {
      logger.warn("tavus faces missing thumbnails", {
        total: options.length,
        withThumbs,
        sampleKeys: Object.keys(raw[0] ?? {}),
      });
    }
  }
  return { options, isLoading: q.isLoading, error: q.error as Error | null };
}

/**
 * Voices from the workspace's ElevenLabs account (private, cloned, and
 * added voices). These are the only voices Tavus can actually apply —
 * `tavus-auto` ignores catalog voice picks, so BYO ElevenLabs is the one
 * supported override path.
 */
export function useElevenLabsVoices(opts?: { enabled?: boolean }) {
  const q = useQuery({
    queryKey: ["tavus", "elevenlabs", "voices"],
    queryFn: () => tavus.listElevenLabsVoices(),
    staleTime: 5 * 60 * 1000,
    enabled: opts?.enabled ?? true,
  });
  const raw = q.data?.voices ?? [];
  const options: ElevenLabsVoiceOption[] = raw
    .map((v): ElevenLabsVoiceOption | null => {
      const id = String(v.voice_id ?? "").trim();
      if (!id) {
        logger.warn("elevenlabs voice missing voice_id", { keys: Object.keys(v) });
        return null;
      }
      const label = String(v.name ?? id).trim();
      const gender =
        typeof v.labels?.gender === "string" ? v.labels.gender : undefined;
      const accent =
        typeof v.labels?.accent === "string" ? v.labels.accent : undefined;
      return { id, label, preview_url: v.preview_url, gender, accent };
    })
    .filter((x): x is ElevenLabsVoiceOption => x !== null);
  return {
    options,
    isLoading: q.isLoading,
    error: q.error as Error | null,
  };
}

/**
 * Resolve the selected Tavus face's gender by matching its `face_name`
 * (case-insensitive) against `voice_name` in Tavus's `/v2/voices`. The
 * replica voice entries carry `male`/`female` in their `tags`. Returns
 * `undefined` when the face is unknown or the tag isn't present.
 */
export function useFaceGender(faceId?: string): "male" | "female" | undefined {
  const faces = useQuery({
    queryKey: TAVUS_FACES_QUERY_KEY,
    queryFn: () => tavus.listFaces(),
    staleTime: 5 * 60 * 1000,
    enabled: !!faceId,
  });
  const voices = useQuery({
    queryKey: ["tavus", "voices"],
    queryFn: () => tavus.listVoices(),
    staleTime: 5 * 60 * 1000,
    enabled: !!faceId,
  });
  if (!faceId) return undefined;
  const face = (faces.data?.faces ?? []).find(
    (f) => String(f.face_id || f.replica_id || "") === faceId,
  );
  const faceName = String(
    face?.face_name || face?.replica_name || face?.name || "",
  )
    .trim()
    .toLowerCase();
  if (!faceName) return undefined;
  const match = (voices.data?.voices ?? []).find(
    (v: TavusVoice) => String(v.voice_name || "").trim().toLowerCase() === faceName,
  );
  const tags = (match?.tags ?? []).map((t) =>
    String(t?.tag_name || "").toLowerCase(),
  );
  if (tags.includes("female")) return "female";
  if (tags.includes("male")) return "male";
  return undefined;
}